import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ShieldCheck, Lock, LogOut } from 'lucide-react';

export default function VerifyPasscode() {
  const [pin, setPin] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { verifyPasscode, logout, userData, isPasscodeVerified } = useAuth();
  const navigate = useNavigate();

  // If already verified, go to home
  useEffect(() => {
    if (isPasscodeVerified) {
       navigate(userData?.role === 'admin' ? '/admin' : '/dashboard');
    }
  }, [isPasscodeVerified, userData, navigate]);

  const handleChange = (index, value) => {
    if (isNaN(value)) return;
    const newPin = [...pin];
    newPin[index] = value.substring(value.length - 1);
    setPin(newPin);

    // Auto-focus next input
    if (value && index < 3) {
      const nextInput = document.getElementById(`pin-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      const prevInput = document.getElementById(`pin-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    const fullPin = pin.join('');
    
    if (fullPin.length < 4) {
      setError('Please enter your 4-digit security PIN.');
      return;
    }

    setLoading(true);
    const isValid = verifyPasscode(fullPin);
    
    if (!isValid) {
      setPin(['', '', '', '']);
      setError('Incorrect security PIN. Please try again.');
      document.getElementById('pin-0')?.focus();
    }
    setLoading(false);
  };

  // Auto-submit when all 4 digits are entered
  useEffect(() => {
    if (pin.every(digit => digit !== '')) {
      handleSubmit();
    }
  }, [pin]);

  return (
    <div className="min-h-screen bg-stripe-bg flex items-center justify-center px-4">
      <div className="max-w-md w-full animate-in fade-in zoom-in duration-500">
        <div className="bg-white rounded-3xl shadow-xl border border-stripe-border p-8 text-center">
          <div className="w-16 h-16 bg-stripe-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-6 text-stripe-blue">
            <Lock size={32} />
          </div>
          
          <h2 className="text-2xl font-black text-stripe-dark tracking-tight">Enter Security PIN</h2>
          <p className="text-sm text-stripe-slate mt-2">Please verify your identities with your 4-digit passcode.</p>

          <form onSubmit={handleSubmit} className="mt-10 space-y-8">
            <div className="flex justify-center gap-4">
              {pin.map((digit, i) => (
                <input
                  key={i}
                  id={`pin-${i}`}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  autoFocus={i === 0}
                  className="w-16 h-20 text-center text-3xl font-black text-stripe-dark bg-stripe-bg border-2 border-stripe-border rounded-2xl focus:border-stripe-blue focus:ring-0 transition-all outline-none"
                />
              ))}
            </div>

            {error && (
              <p className="text-xs font-bold text-red-500 bg-red-50 py-2 rounded-lg border border-red-100 animate-in slide-in-from-top-2">
                {error}
              </p>
            )}

            <button 
              type="submit" 
              disabled={loading || pin.some(d => !d)}
              className="w-full btn-primary py-4 text-xs font-black uppercase tracking-[0.2em] shadow-lg shadow-primary-600/20 active:scale-95 transition-all disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Access My Account'}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-stripe-border flex items-center justify-center gap-2">
             <button 
               onClick={() => logout().then(() => navigate('/login'))}
               className="flex items-center gap-2 text-stripe-slate hover:text-stripe-dark font-bold text-[10px] uppercase tracking-widest transition-colors"
             >
               <LogOut size={14} /> Switch Account
             </button>
          </div>
        </div>
        
        <p className="text-center text-[10px] text-stripe-slate font-medium mt-8 flex items-center justify-center gap-1">
          <ShieldCheck size={12} /> Secure session protected by AES-256 and 2FA
        </p>
      </div>
    </div>
  );
}
